import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchRoute } from "../../../api/apiFetchRoute";
import {
  enumcountryToI18nKey,
  enumsectionToI18nKey,
} from "../../../generated/enums";
import { RequestTypeOf } from "../../../generated/routes";
import { Company } from "../../../generated/types";
import { Button } from "../../common/Button";
import { DisplayValue } from "../../common/DisplayValue";
import { FormInput } from "../../common/FormLayout";
import { LabelParam } from "../../common/LabelParam";
import { sectionToIcon } from "../../type/default";

export const CompanySection = ({
  company,
  detail = false,
  onUpdate,
}: {
  company: Company;
  detail?: boolean;
  onUpdate?: () => void;
}) => {
  const { t } = useTranslation();
  // Affiche l'enregistrement en attendant que le parent renvoie les données à jour.
  const [savedCompany, setSavedCompany] = useState<Company | null>(null);
  const theCompany = savedCompany ?? company;
  const [isEditing, setIsEditing] = useState(false);
  const [editedCompany, setEditedCompany] = useState(company);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    setSavedCompany(null);
  }, [company]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedCompany((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setSaveFailed(false);
    try {
      type CompanyRequest = RequestTypeOf<"putCompanies">;

      const body: CompanyRequest = editedCompany;
      const response = await apiFetchRoute("putCompanies", body);

      if (!response.ok) {
        throw new Error("Erreur lors de la mise à jour de l'entreprise");
      }
      setIsEditing(false);
      setSavedCompany(editedCompany);
      onUpdate?.();
    } catch (error) {
      setSaveFailed(true);
      console.error("Erreur lors de la mise à jour:", error);
    }
  };

  const startEditing = () => {
    setEditedCompany(theCompany);
    setSaveFailed(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setSaveFailed(false);
    setIsEditing(false);
  };
  return (
    <div>
      <div className="flex justify-between border-b border-grey-light mb-4">
        <div className="flex gap-4 items-center">
          <FontAwesomeIcon icon={sectionToIcon.section_company} />
          <h3 className="text-lg font-oxaniumMedium">
            {t(enumsectionToI18nKey["section_company"])}
          </h3>
        </div>
        {detail && !isEditing && (
          <button
            onClick={startEditing}
            className="text-grey-medium hover:text-secondary"
          >
            <FontAwesomeIcon icon="pen-to-square" className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="space-y-4">
        {isEditing ? (
          <LabelParam label={t("company-name")}>
            <FormInput
              value={editedCompany.company_name || ""}
              onChange={handleChange}
              name="company_name"
              sizeInput="small"
              className="bg-transparent border-none p-0 text-sm"
            />
          </LabelParam>
        ) : (
          <LabelParam label={t("company-name")}>
            <DisplayValue value={theCompany.company_name ?? ""} />
          </LabelParam>
        )}

        {detail && (
          <div className="space-y-4">
            {isEditing ? (
              <LabelParam label={t("company-address")}>
                <FormInput
                  value={editedCompany.address || ""}
                  onChange={handleChange}
                  name="address"
                  sizeInput="small"
                  className="bg-transparent border-none p-0 text-sm"
                />
              </LabelParam>
            ) : (
              <LabelParam label={t("company-address")}>
                <DisplayValue value={theCompany.address ?? ""} />
              </LabelParam>
            )}
            {company.address_complementary &&
              (isEditing ? (
                <LabelParam label={t("company-address-complementary")}>
                  <FormInput
                    value={editedCompany.address_complementary || ""}
                    onChange={handleChange}
                    name="address_complementary"
                    sizeInput="small"
                    className="bg-transparent border-none p-0 text-sm"
                  />
                </LabelParam>
              ) : (
                <LabelParam label={t("company-address-complementary")}>
                  <DisplayValue
                    value={theCompany.address_complementary ?? ""}
                  />
                </LabelParam>
              ))}
          </div>
        )}

        {isEditing ? (
          <LabelParam label={t("company-city")}>
            <FormInput
              value={editedCompany.city || ""}
              onChange={handleChange}
              name="city"
              sizeInput="small"
              className="bg-transparent border-none p-0 text-sm"
            />
          </LabelParam>
        ) : (
          <LabelParam label={t("company-city")}>
            <DisplayValue value={theCompany.city ?? ""} />
          </LabelParam>
        )}

        {isEditing ? (
          <LabelParam label={t("company-zip-code")}>
            <FormInput
              value={editedCompany.zip_code || ""}
              onChange={handleChange}
              name="zip_code"
              sizeInput="small"
              className="bg-transparent border-none p-0 text-sm"
            />
          </LabelParam>
        ) : (
          <LabelParam label={t("company-zip-code")}>
            <DisplayValue value={theCompany.zip_code ?? ""} />
          </LabelParam>
        )}

        {/* Liste fermée de codes à deux lettres : pas de saisie libre. */}
        <LabelParam label={t("company-country")}>
          <DisplayValue
            value={
              theCompany.country
                ? t(enumcountryToI18nKey[theCompany.country])
                : "-"
            }
          />
        </LabelParam>

        {detail && (
          <div className="space-y-4">
            {isEditing ? (
              <LabelParam label={t("company-siret")}>
                <FormInput
                  value={editedCompany.siret || ""}
                  onChange={handleChange}
                  name="siret"
                  sizeInput="small"
                  className="bg-transparent border-none p-0 text-sm"
                />
              </LabelParam>
            ) : (
              <LabelParam label={t("company-siret")}>
                <DisplayValue value={theCompany.siret ?? ""} />
              </LabelParam>
            )}

            <LabelParam label={t("company-x3-code")}>
              <DisplayValue value={theCompany.id_x3 ?? ""} />
            </LabelParam>

            {isEditing ? (
              <LabelParam label={t("company-email")}>
                <FormInput
                  value={editedCompany.email || ""}
                  onChange={handleChange}
                  name="email"
                  sizeInput="small"
                  className="bg-transparent border-none p-0 text-sm"
                />
              </LabelParam>
            ) : (
              <LabelParam label={t("company-email")}>
                <DisplayValue value={theCompany.email ?? ""} />
              </LabelParam>
            )}

            {isEditing ? (
              <LabelParam label={t("company-phone-mobile")}>
                <FormInput
                  value={editedCompany.phone_mobile || ""}
                  onChange={handleChange}
                  name="phone_mobile"
                  sizeInput="small"
                  className="bg-transparent border-none p-0 text-sm"
                />
              </LabelParam>
            ) : (
              <LabelParam label={t("company-phone-mobile")}>
                <DisplayValue value={theCompany.phone_mobile ?? ""} />
              </LabelParam>
            )}

            {isEditing ? (
              <LabelParam label={t("company-phone-fixe")}>
                <FormInput
                  value={editedCompany.phone_fixe || ""}
                  onChange={handleChange}
                  name="phone_fixe"
                  sizeInput="small"
                  className="bg-transparent border-none p-0 text-sm"
                />
              </LabelParam>
            ) : (
              <LabelParam label={t("company-phone-fixe")}>
                <DisplayValue value={theCompany.phone_fixe ?? ""} />
              </LabelParam>
            )}
          </div>
        )}
        {isEditing && saveFailed && (
          <p className="text-xs text-warning-readable">
            {t("ticket-update-error")}
          </p>
        )}
        {isEditing && (
          <div className="flex justify-end gap-4">
            <Button type="button" variant="secondary" onClick={handleCancel}>
              {t("button-cancel")}
            </Button>
            <Button type="submit" onClick={handleSubmit}>
              {t("button-save")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
